import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Plus, Pencil, Trash2, X, Crown, Eye, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import { ColorPicker, SizePicker } from '../../components/ColorSizePicker';
import { ImageUpload, API_URL } from './shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const BrandsTab = ({ token, searchQuery }) => {
  const [brands, setBrands] = useState([]);
  const [brandSections, setBrandSections] = useState([]);
  const [brandProducts, setBrandProducts] = useState([]);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showBrandSectionModal, setShowBrandSectionModal] = useState(false);
  const [showBrandProductModal, setShowBrandProductModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editingBrandSection, setEditingBrandSection] = useState(null);
  const [editingBrandProduct, setEditingBrandProduct] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [brandForm, setBrandForm] = useState({
    name: '', slug: '', description: '', hero_type: 'image', hero_image: '', hero_video: '',
    hero_title: '', hero_subtitle: '', hero_cta_text: '', hero_cta_link: '', is_active: true, show_in_nav: true, nav_order: 100
  });
  const [brandSectionForm, setBrandSectionForm] = useState({
    brand_id: '', section_type: 'text', title: '', content: '', media_url: '', media_urls: [], order: 0, is_active: true
  });
  const [brandProductForm, setBrandProductForm] = useState({
    brand_id: '', name: '', description: '', specifications: '', images: [], price: 0, cost_price: '', price_on_request: false,
    category: '', in_stock: true, featured: false, sizes: [], size_type: 'letter', available_colors: [], custom_colors: [], order: 0
  });
  const [brandProductImages, setBrandProductImages] = useState([]);
  const [videoUrlInput, setVideoUrlInput] = useState('');

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(res.data);
    } catch (error) {
      toast.error('Failed to load brands');
    }
  };

  const fetchBrandSections = async (brandId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/brands/${brandId}/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrandSections(res.data);
    } catch (error) {
      console.error('Error fetching brand sections:', error);
    }
  };

  const fetchBrandProducts = async (brandId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/brand-products?brand_id=${brandId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrandProducts(res.data);
    } catch (error) {
      console.error('Error fetching brand products:', error);
    }
  };

  const handleBrandSubmit = async () => {
    try {
      if (editingBrand) {
        await axios.patch(`${API_URL}/admin/brands/${editingBrand.id}`, brandForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Brand updated');
      } else {
        await axios.post(`${API_URL}/admin/brands`, brandForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Brand created');
      }
      setShowBrandModal(false);
      setEditingBrand(null);
      setBrandForm({ name: '', slug: '', description: '', hero_type: 'image', hero_image: '', hero_video: '',
        hero_title: '', hero_subtitle: '', hero_cta_text: '', hero_cta_link: '', is_active: true, show_in_nav: true, nav_order: 100 });
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save brand');
    }
  };

  const deleteBrand = async (id) => {
    if (!window.confirm('Delete this brand and ALL its sections/products? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/admin/brands/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Brand deleted');
      fetchBrands();
    } catch (error) {
      toast.error('Failed to delete brand');
    }
  };

  const handleBrandSectionSubmit = async () => {
    try {
      const data = { ...brandSectionForm, brand_id: selectedBrandId };
      if (editingBrandSection) {
        await axios.patch(`${API_URL}/admin/brand-sections/${editingBrandSection.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Section updated');
      } else {
        await axios.post(`${API_URL}/admin/brand-sections`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Section created');
      }
      setShowBrandSectionModal(false);
      setEditingBrandSection(null);
      setBrandSectionForm({ brand_id: '', section_type: 'text', title: '', content: '', media_url: '', media_urls: [], order: 0, is_active: true });
      fetchBrandSections(selectedBrandId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save section');
    }
  };

  const deleteBrandSection = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await axios.delete(`${API_URL}/admin/brand-sections/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Section deleted');
      fetchBrandSections(selectedBrandId);
    } catch (error) {
      toast.error('Failed to delete section');
    }
  };

  const reorderBrandSection = async (sectionId, direction) => {
    const sortedSections = [...brandSections].sort((a, b) => a.order - b.order);
    const currentIndex = sortedSections.findIndex(s => s.id === sectionId);
    const targetIndex = currentIndex + direction;
    
    if (targetIndex < 0 || targetIndex >= sortedSections.length) return;
    
    const currentSection = sortedSections[currentIndex];
    const targetSection = sortedSections[targetIndex];
    
    try {
      await Promise.all([
        axios.patch(`${API_URL}/admin/brand-sections/${currentSection.id}`, 
          { order: targetSection.order }, 
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.patch(`${API_URL}/admin/brand-sections/${targetSection.id}`, 
          { order: currentSection.order }, 
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);
      toast.success('Section reordered');
      fetchBrandSections(selectedBrandId);
    } catch (error) {
      toast.error('Failed to reorder section');
    }
  };

  const handleBrandProductSubmit = async () => {
    try {
      const data = { 
        ...brandProductForm, 
        brand_id: selectedBrandId, 
        images: brandProductImages,
        cost_price: brandProductForm.cost_price ? parseFloat(brandProductForm.cost_price) : null,
        sizes: brandProductForm.sizes?.length > 0 ? brandProductForm.sizes : null,
        size_type: brandProductForm.size_type || null,
        available_colors: brandProductForm.available_colors?.length > 0 ? brandProductForm.available_colors : null,
        custom_colors: brandProductForm.custom_colors?.length > 0 ? brandProductForm.custom_colors : null
      };
      if (editingBrandProduct) {
        await axios.patch(`${API_URL}/admin/brand-products/${editingBrandProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API_URL}/admin/brand-products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created');
      }
      setShowBrandProductModal(false);
      setEditingBrandProduct(null);
      setBrandProductForm({ brand_id: '', name: '', description: '', specifications: '', images: [], price: 0, cost_price: '', price_on_request: false,
        category: '', in_stock: true, featured: false, sizes: [], size_type: 'letter', available_colors: [], custom_colors: [], order: 0 });
      setBrandProductImages([]);
      fetchBrandProducts(selectedBrandId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const deleteBrandProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/admin/brand-products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchBrandProducts(selectedBrandId);
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const addBrandProductImage = (url) => {
    if (url && !brandProductImages.includes(url)) {
      setBrandProductImages([...brandProductImages, url]);
    }
  };

  const removeBrandProductImage = (index) => {
    setBrandProductImages(brandProductImages.filter((_, i) => i !== index));
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Brand Management</h2>
          <Button onClick={() => { setEditingBrand(null); setBrandForm({ name: '', slug: '', description: '', hero_type: 'image', hero_image: '', hero_video: '', hero_title: '', hero_subtitle: '', hero_cta_text: '', hero_cta_link: '', is_active: true, show_in_nav: true, nav_order: 100 }); setShowBrandModal(true); }} data-testid="add-brand-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Brand
          </Button>
        </div>

        {/* Brands List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {brands.map((brand) => (
            <div key={brand.id} className="bg-white rounded-lg border overflow-hidden">
              {brand.hero_image && (
                <div className="h-32 overflow-hidden">
                  <img src={brand.hero_image} alt={brand.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#0A2342]">{brand.name}</h3>
                  <div className="flex gap-1">
                    {brand.is_active ? (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                    {brand.show_in_nav && (
                      <Badge className="bg-blue-100 text-blue-700">In Nav</Badge>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-2">/{brand.slug}</p>
                <p className="text-sm text-gray-600 line-clamp-2 mb-4">{brand.description || 'No description'}</p>
                
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingBrand(brand);
                    setBrandForm({
                      name: brand.name, slug: brand.slug, description: brand.description || '',
                      hero_type: brand.hero_type || 'image', hero_image: brand.hero_image || '', hero_video: brand.hero_video || '',
                      hero_title: brand.hero_title || '', hero_subtitle: brand.hero_subtitle || '',
                      hero_cta_text: brand.hero_cta_text || '', hero_cta_link: brand.hero_cta_link || '',
                      is_active: brand.is_active, show_in_nav: brand.show_in_nav, nav_order: brand.nav_order || 100
                    });
                    setShowBrandModal(true);
                  }}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    setSelectedBrandId(brand.id);
                    fetchBrandSections(brand.id);
                    fetchBrandProducts(brand.id);
                  }}>
                    <Eye className="w-3 h-3 mr-1" /> Manage
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteBrand(brand.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {brands.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border">
            <Crown className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Brands Yet</h3>
            <p className="text-gray-500 mb-4">Create your first brand to get started</p>
            <Button onClick={() => setShowBrandModal(true)}>
              <Plus className="w-4 h-4 mr-2" /> Add Brand
            </Button>
          </div>
        )}

        {/* Selected Brand Management */}
        {selectedBrandId && (
          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[#0A2342]">
                Managing: {brands.find(b => b.id === selectedBrandId)?.name}
              </h3>
              <Button size="sm" variant="outline" onClick={() => { setSelectedBrandId(null); setBrandSections([]); setBrandProducts([]); }}>
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>

            {/* Sections */}
            <div className="bg-white rounded-lg border p-4 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium">Content Sections</h4>
                <Button size="sm" onClick={() => {
                  setEditingBrandSection(null);
                  setBrandSectionForm({ brand_id: selectedBrandId, section_type: 'text', title: '', content: '', media_url: '', media_urls: [], order: brandSections.length, is_active: true });
                  setShowBrandSectionModal(true);
                }}>
                  <Plus className="w-3 h-3 mr-1" /> Add Section
                </Button>
              </div>
              
              {brandSections.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No sections yet. Add text, images, or videos.</p>
              ) : (
                <div className="space-y-2">
                  {brandSections.sort((a, b) => a.order - b.order).map((section) => (
                    <div key={section.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-xs bg-[#0A2342] text-white px-2 py-1 rounded">{section.order}</span>
                        <div>
                          <span className="font-medium text-sm">{section.title || `${section.section_type} section`}</span>
                          <Badge variant="outline" className="ml-2 text-xs">{section.section_type}</Badge>
                          {!section.is_active && <Badge variant="outline" className="ml-1 text-xs text-red-600">Hidden</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => reorderBrandSection(section.id, -1)}
                          disabled={brandSections.sort((a, b) => a.order - b.order).findIndex(s => s.id === section.id) === 0}
                          title="Move Up"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => reorderBrandSection(section.id, 1)}
                          disabled={brandSections.sort((a, b) => a.order - b.order).findIndex(s => s.id === section.id) === brandSections.length - 1}
                          title="Move Down"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => {
                          setEditingBrandSection(section);
                          setBrandSectionForm({
                            brand_id: section.brand_id, section_type: section.section_type, title: section.title || '',
                            content: section.content || '', media_url: section.media_url || '', media_urls: section.media_urls || [],
                            order: section.order, is_active: section.is_active
                          });
                          setShowBrandSectionModal(true);
                        }}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteBrandSection(section.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Products */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium">Brand Products</h4>
                <Button size="sm" onClick={() => {
                  setEditingBrandProduct(null);
                  setBrandProductForm({ brand_id: selectedBrandId, name: '', description: '', specifications: '', images: [], price: 0, cost_price: '', price_on_request: false, category: '', in_stock: true, featured: false, order: brandProducts.length });
                  setBrandProductImages([]);
                  setShowBrandProductModal(true);
                }}>
                  <Plus className="w-3 h-3 mr-1" /> Add Product
                </Button>
              </div>
              
              {brandProducts.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No products yet. Add products for this brand.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {brandProducts.map((product) => (
                    <div key={product.id} className="border rounded-lg overflow-hidden">
                      {product.images?.[0] && (
                        <div className="h-32 overflow-hidden">
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-3">
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="font-medium text-sm">{product.name}</h5>
                          {product.featured && <Badge className="bg-[#FF6B35] text-xs">Featured</Badge>}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {product.price_on_request ? 'Price on Request' : `R${product.price?.toLocaleString()}`}
                        </p>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditingBrandProduct(product);
                            setBrandProductForm({
                              brand_id: product.brand_id, name: product.name, description: product.description,
                              specifications: product.specifications || '', images: product.images || [],
                              price: product.price, cost_price: product.cost_price?.toString() || '', price_on_request: product.price_on_request,
                              category: product.category || '', in_stock: product.in_stock, featured: product.featured,
                              sizes: product.sizes || [], size_type: product.size_type || 'letter',
                              available_colors: product.available_colors || [], custom_colors: product.custom_colors || [],
                              order: product.order
                            });
                            setBrandProductImages(product.images || []);
                            setShowBrandProductModal(true);
                          }}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteBrandProduct(product.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Brand Modal */}
      <Dialog open={showBrandModal} onOpenChange={setShowBrandModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name *</label>
                <Input placeholder="e.g. DiveVolk" value={brandForm.name} onChange={(e) => setBrandForm({...brandForm, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug *</label>
                <Input placeholder="e.g. divevolk" value={brandForm.slug} onChange={(e) => setBrandForm({...brandForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})} />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea className="w-full border rounded-md p-2 text-sm" rows={2} placeholder="Brand description" value={brandForm.description} onChange={(e) => setBrandForm({...brandForm, description: e.target.value})} />
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Hero Section</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hero Type</label>
                  <Select value={brandForm.hero_type} onValueChange={(v) => setBrandForm({...brandForm, hero_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Image Background</SelectItem>
                      <SelectItem value="video">Video Background</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hero Image</label>
                  <ImageUpload value={brandForm.hero_image} onChange={(url) => setBrandForm({...brandForm, hero_image: url})} token={token} />
                </div>
                
                {brandForm.hero_type === 'video' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hero Video URL</label>
                    <Input placeholder="YouTube link or video file URL" value={brandForm.hero_video} onChange={(e) => setBrandForm({...brandForm, hero_video: e.target.value})} />
                    <p className="text-xs text-gray-500 mt-1">Supports: YouTube links (youtube.com, youtu.be) or direct video files (.mp4)</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hero Title</label>
                    <Input placeholder="Brand headline" value={brandForm.hero_title} onChange={(e) => setBrandForm({...brandForm, hero_title: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hero Subtitle</label>
                    <Input placeholder="Tagline or subtitle" value={brandForm.hero_subtitle} onChange={(e) => setBrandForm({...brandForm, hero_subtitle: e.target.value})} />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                    <Input placeholder="e.g. Shop Now" value={brandForm.hero_cta_text} onChange={(e) => setBrandForm({...brandForm, hero_cta_text: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
                    <Input placeholder="e.g. #products" value={brandForm.hero_cta_link} onChange={(e) => setBrandForm({...brandForm, hero_cta_link: e.target.value})} />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Settings</h4>
              <div className="grid grid-cols-3 gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={brandForm.is_active} onChange={(e) => setBrandForm({...brandForm, is_active: e.target.checked})} />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={brandForm.show_in_nav} onChange={(e) => setBrandForm({...brandForm, show_in_nav: e.target.checked})} />
                  <span className="text-sm">Show in Navigation</span>
                </label>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Nav Order</label>
                  <Input type="number" value={brandForm.nav_order} onChange={(e) => setBrandForm({...brandForm, nav_order: parseInt(e.target.value) || 100})} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBrandModal(false)}>Cancel</Button>
            <Button onClick={handleBrandSubmit}>{editingBrand ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brand Section Modal */}
      <Dialog open={showBrandSectionModal} onOpenChange={setShowBrandSectionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingBrandSection ? 'Edit Section' : 'Add Section'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Type</label>
              <Select value={brandSectionForm.section_type} onValueChange={(v) => setBrandSectionForm({...brandSectionForm, section_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="products">Products Grid</SelectItem>
                  <SelectItem value="text">Text Block</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="gallery">Image Gallery</SelectItem>
                </SelectContent>
              </Select>
              {brandSectionForm.section_type === 'products' && (
                <p className="text-xs text-gray-500 mt-1">This section will display all products for this brand. Place it directly after the hero section for best results.</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
              <Input placeholder="Section heading (optional)" value={brandSectionForm.title} onChange={(e) => setBrandSectionForm({...brandSectionForm, title: e.target.value})} />
            </div>
            
            {brandSectionForm.section_type === 'text' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea className="w-full border rounded-md p-2 text-sm" rows={5} placeholder="Enter text content" value={brandSectionForm.content} onChange={(e) => setBrandSectionForm({...brandSectionForm, content: e.target.value})} />
              </div>
            )}
            
            {(brandSectionForm.section_type === 'image' || brandSectionForm.section_type === 'video') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {brandSectionForm.section_type === 'image' ? 'Media URL' : 'Video URLs (add multiple for side-by-side display)'}
                </label>
                {brandSectionForm.section_type === 'image' ? (
                  <ImageUpload value={brandSectionForm.media_url} onChange={(url) => setBrandSectionForm({...brandSectionForm, media_url: url})} token={token} />
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Paste YouTube or video URL..." 
                        value={videoUrlInput} 
                        onChange={(e) => setVideoUrlInput(e.target.value)} 
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          if (videoUrlInput.trim()) {
                            setBrandSectionForm(prev => ({
                              ...prev,
                              media_urls: [...(prev.media_urls || []), videoUrlInput.trim()]
                            }));
                            setVideoUrlInput('');
                          }
                        }}
                        disabled={!videoUrlInput.trim()}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    
                    {brandSectionForm.media_urls?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500">Added videos ({brandSectionForm.media_urls.length}):</p>
                        {brandSectionForm.media_urls.map((url, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded-md">
                            <span className="text-xs text-gray-600 truncate flex-1">{url}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setBrandSectionForm(prev => ({
                                  ...prev,
                                  media_urls: prev.media_urls.filter((_, i) => i !== idx)
                                }));
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {!brandSectionForm.media_urls?.length && (
                      <div className="text-xs text-gray-500">
                        <p>Or use single video (legacy):</p>
                        <Input 
                          placeholder="Single video URL" 
                          value={brandSectionForm.media_url} 
                          onChange={(e) => setBrandSectionForm({...brandSectionForm, media_url: e.target.value})} 
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <Input type="number" value={brandSectionForm.order} onChange={(e) => setBrandSectionForm({...brandSectionForm, order: parseInt(e.target.value) || 0})} />
              </div>
              <label className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={brandSectionForm.is_active} onChange={(e) => setBrandSectionForm({...brandSectionForm, is_active: e.target.checked})} />
                <span className="text-sm">Visible</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBrandSectionModal(false)}>Cancel</Button>
            <Button onClick={handleBrandSectionSubmit}>{editingBrandSection ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brand Product Modal */}
      <Dialog open={showBrandProductModal} onOpenChange={setShowBrandProductModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingBrandProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
              <Input placeholder="Product name" value={brandProductForm.name} onChange={(e) => setBrandProductForm({...brandProductForm, name: e.target.value})} />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea className="w-full border rounded-md p-2 text-sm" rows={3} placeholder="Product description" value={brandProductForm.description} onChange={(e) => setBrandProductForm({...brandProductForm, description: e.target.value})} />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specifications / Additional Info</label>
              <textarea className="w-full border rounded-md p-2 text-sm" rows={3} placeholder="Technical specs, materials, dimensions, etc." value={brandProductForm.specifications} onChange={(e) => setBrandProductForm({...brandProductForm, specifications: e.target.value})} />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <Input placeholder="e.g. Masks, BCDs, Regulators" value={brandProductForm.category} onChange={(e) => setBrandProductForm({...brandProductForm, category: e.target.value})} />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price (R)</label>
                <Input type="number" placeholder="0" value={brandProductForm.price} onChange={(e) => setBrandProductForm({...brandProductForm, price: parseFloat(e.target.value) || 0})} disabled={brandProductForm.price_on_request} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (R) - Admin Only</label>
                <Input type="number" placeholder="0" value={brandProductForm.cost_price} onChange={(e) => setBrandProductForm({...brandProductForm, cost_price: e.target.value})} />
              </div>
            </div>
            
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={brandProductForm.price_on_request} onChange={(e) => setBrandProductForm({...brandProductForm, price_on_request: e.target.checked})} />
              <span className="text-sm">Price on Request</span>
            </label>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Images</label>
              <div className="space-y-2">
                {brandProductImages.map((url, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <img src={url} alt={`Product ${idx + 1}`} className="w-16 h-16 object-cover rounded" />
                    <span className="flex-1 text-sm text-gray-600 truncate">{url}</span>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeBrandProductImage(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <ImageUpload value="" onChange={(url) => addBrandProductImage(url)} token={token} />
              </div>
            </div>
            
            <div className="border rounded-lg p-4 bg-gray-50">
              <ColorPicker
                selectedColors={brandProductForm.available_colors || []}
                customColors={brandProductForm.custom_colors || []}
                onColorsChange={(colors) => setBrandProductForm({...brandProductForm, available_colors: colors})}
                onCustomColorsChange={(colors) => setBrandProductForm({...brandProductForm, custom_colors: colors})}
              />
            </div>
            
            <div className="border rounded-lg p-4 bg-gray-50">
              <SizePicker
                selectedSizes={brandProductForm.sizes || []}
                sizeType={brandProductForm.size_type || 'letter'}
                onSizesChange={(sizes) => setBrandProductForm({...brandProductForm, sizes: sizes})}
                onSizeTypeChange={(type) => setBrandProductForm({...brandProductForm, size_type: type, sizes: []})}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={brandProductForm.in_stock} onChange={(e) => setBrandProductForm({...brandProductForm, in_stock: e.target.checked})} />
                <span className="text-sm">In Stock</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={brandProductForm.featured} onChange={(e) => setBrandProductForm({...brandProductForm, featured: e.target.checked})} />
                <span className="text-sm">Featured</span>
              </label>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Order</label>
                <Input type="number" value={brandProductForm.order} onChange={(e) => setBrandProductForm({...brandProductForm, order: parseInt(e.target.value) || 0})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBrandProductModal(false)}>Cancel</Button>
            <Button onClick={handleBrandProductSubmit}>{editingBrandProduct ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BrandsTab;
